import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight, Users, BarChart3, ShieldCheck, BookOpen, Crown, Check, Building2, Mail,
} from 'lucide-react';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '@shared/services/firebase';
import { RidgeTexture } from '../components/ui/RidgeTexture';
import { Aurora, Reveal, SectionDivider } from '../components/ui/depth';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/Button';

function Hero() {
  return (
    <section className="relative pt-40 pb-12 px-4 text-center overflow-hidden">
      <Aurora palette="mixed" intensity={0.8} />
      <div className="max-w-3xl mx-auto relative">
        <Reveal>
          <span className="inline-flex items-center gap-2 mb-5 px-3 py-1 rounded-pill text-xs font-semibold bg-indigo-500/12 text-indigo-300 border border-indigo-500/30">
            <Building2 size={12} /> CogniPrint for Teams
          </span>
        </Reveal>
        <Reveal>
          <h1 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight mb-5 leading-[1.1]">
            Personalised learning —{' '}
            <span className="bg-gradient-to-r from-indigo-400 to-gold-500 bg-clip-text text-transparent">for all 800 of them.</span>
          </h1>
        </Reveal>
        <Reveal>
          <p className="text-content-secondary font-body text-lg leading-relaxed mb-8 max-w-2xl mx-auto">
            Compliance training employees click through. Onboarding material nobody remembers. Leadership development that can't be measured.
            CogniPrint builds a unique learning experience for every person on your team, from the same source material.
          </p>
        </Reveal>
        <Reveal>
          <a href="#demo" className="inline-block">
            <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.98 }}>
              <Button variant="gold" size="lg">
                Book a 20-minute walkthrough <ArrowRight size={16} />
              </Button>
            </motion.div>
          </a>
        </Reveal>
      </div>
    </section>
  );
}

interface ValueRow {
  icon: typeof Users;
  title: string;
  body: string;
}

const valueRows: ValueRow[] = [
  {
    icon: BarChart3,
    title: 'Real learning signals, not completion rates',
    body: 'Who actually understood the material? Where are the gaps? CogniPrint tracks mastery per concept per learner — the metric your L&D budget was supposed to prove all along.',
  },
  {
    icon: Users,
    title: 'Adaptive for every learner, without a curriculum designer',
    body: "Each team member's learning path is shaped by their CogniPrint profile. Your one compliance guide becomes 30 differently-sequenced, differently-formatted, differently-paced experiences — generated automatically.",
  },
  {
    icon: BookOpen,
    title: 'Upload once, personalised for everyone',
    body: 'Push your training materials to the team. CogniPrint handles the personalisation. Version updates propagate to new assignments without breaking existing learning paths.',
  },
  {
    icon: ShieldCheck,
    title: 'Built for regulated environments',
    body: 'POPIA and GDPR compliant by design. Encryption in transit and at rest. DPA available on request. SSO on the roadmap for Phase 6.',
  },
];

function ValueSection() {
  return (
    <section className="relative py-16 px-4">
      <div className="max-w-5xl mx-auto">
        <Reveal className="text-center mb-12 max-w-2xl mx-auto">
          <h2 className="font-heading font-bold text-3xl sm:text-4xl mb-3">
            Why L&amp;D teams pilot with us
          </h2>
          <p className="text-content-secondary font-body">
            Three weeks from first upload to measurable learning outcomes.
          </p>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-5">
          {valueRows.map((row) => (
            <Reveal key={row.title}>
              <div className="h-full rounded-2xl border border-dark-border bg-dark-surface/60 p-6">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center mb-4 shadow-glow-indigo">
                  <row.icon size={22} className="text-white" />
                </div>
                <h3 className="font-heading font-semibold text-xl mb-2">{row.title}</h3>
                <p className="text-sm text-content-secondary font-body leading-relaxed">{row.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function PilotSection() {
  return (
    <section className="relative py-16 px-4">
      <div className="max-w-4xl mx-auto">
        <Reveal className="text-center mb-10">
          <h2 className="font-heading font-bold text-3xl sm:text-4xl mb-3">How a pilot works</h2>
          <p className="text-content-secondary font-body">30 days. 20–30 users. One success metric you choose.</p>
        </Reveal>

        <div className="grid md:grid-cols-3 gap-5">
          {[
            { n: '01', title: 'Scoping call', body: 'We pick one measurable outcome — time-to-competence for new hires, compliance pass rate, leadership programme completion — and agree the cohort.' },
            { n: '02', title: 'Upload & provision', body: 'You send the source materials. We provision the accounts. Your team takes the 5-minute CogniPrint diagnostic and starts learning the next day.' },
            { n: '03', title: 'Outcomes review', body: 'After 30 days we sit down together and look at the numbers. If it worked, we scale. If it didn\'t, we walk away — no contract penalties.' },
          ].map((step) => (
            <Reveal key={step.n}>
              <div className="h-full rounded-2xl border border-dark-border bg-dark-surface/60 p-6 relative">
                <div className="absolute -top-4 left-6 px-3 py-0.5 rounded-pill text-xs font-body font-bold tracking-wider bg-gold-500 text-dark-bg">
                  {step.n}
                </div>
                <h3 className="font-heading font-semibold text-lg mt-3 mb-2">{step.title}</h3>
                <p className="text-sm text-content-secondary font-body leading-relaxed">{step.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function PricingSection() {
  return (
    <section className="relative py-16 px-4">
      <div className="max-w-4xl mx-auto">
        <Reveal className="text-center mb-8">
          <h2 className="font-heading font-bold text-3xl sm:text-4xl mb-3">Pricing</h2>
        </Reveal>

        <Reveal>
          <div className="rounded-2xl border border-indigo-500/30 bg-dark-surface/80 p-6 sm:p-8 shadow-glow-indigo">
            <div className="flex items-baseline justify-between flex-wrap gap-4 mb-4">
              <div>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-pill text-[11px] font-semibold bg-indigo-500/15 text-indigo-300">
                  <Crown size={11} /> Teams
                </span>
                <h3 className="font-heading text-2xl font-bold mt-2">$19.99<span className="text-content-muted font-body text-base"> / seat / month</span></h3>
                <p className="text-xs text-content-dim font-body mt-1">ZAR invoicing available · Annual billing on the roadmap</p>
              </div>
              <a href="#demo">
                <Button>Request pricing quote</Button>
              </a>
            </div>

            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 mt-6">
              {[
                'Everything in Pro, per seat',
                '5 uploads / seat / month ($1.49 each for more)',
                'Unlimited Professor Cogni queries',
                'Cohort leaderboards',
                'Instructor view: aggregate mastery (coming Phase 6)',
                'Cohort challenges',
                'Coach seat pool (admin-assigned, +$7.99/seat)',
                'Priority processing',
                'All formats export + team reports',
              ].map((feature) => (
                <div key={feature} className="flex items-start gap-2 text-sm font-body text-content-secondary">
                  <Check size={14} className="text-success shrink-0 mt-1" />
                  <span>{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal className="mt-6 text-center">
          <p className="text-sm text-content-muted font-body">
            Enterprise features (SSO, SCIM, audit logs, annual contracts) are available on larger rollouts — let's talk about what your procurement team needs.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

interface DemoFormFields {
  name: string;
  email: string;
  company: string;
  role: string;
  teamSize: string;
  useCase: string;
  timeline: string;
}

const initialForm: DemoFormFields = {
  name: '', email: '', company: '', role: '',
  teamSize: '20-50', useCase: '', timeline: 'within_quarter',
};

function DemoForm() {
  const [form, setForm] = useState<DemoFormFields>(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await addDoc(collection(db, 'teamsEnquiries'), {
        ...form,
        submittedAt: serverTimestamp(),
        source: 'for-teams-landing',
      });
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      setError('Something went wrong. Please email hello@cogniprint.io instead.');
    } finally {
      setSubmitting(false);
    }
  }

  function update<K extends keyof DemoFormFields>(key: K, value: DemoFormFields[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  const inputClass =
    'w-full px-3 py-2 rounded-lg bg-dark-subtle border border-dark-border text-content text-sm font-body focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/40 transition-colors';

  return (
    <section id="demo" className="relative py-16 px-4">
      <div className="max-w-2xl mx-auto">
        <Reveal className="text-center mb-8">
          <h2 className="font-heading font-bold text-3xl sm:text-4xl mb-3">Book a walkthrough</h2>
          <p className="text-content-secondary font-body">
            20 minutes. We'll show you the product with your own use case in mind, and answer security / procurement questions.
          </p>
        </Reveal>

        <Reveal>
          <div className="rounded-2xl border border-dark-border bg-dark-surface/80 p-6 sm:p-8">
            {submitted ? (
              <div className="text-center py-6">
                <div className="w-14 h-14 rounded-full bg-success/15 flex items-center justify-center mx-auto mb-4">
                  <Check size={26} className="text-success" />
                </div>
                <h3 className="font-heading font-bold text-xl mb-2">Thanks — we'll be in touch</h3>
                <p className="text-sm text-content-secondary font-body mb-4">
                  We'll email you within one business day to schedule the walkthrough. If you need us sooner,{' '}
                  <a href="mailto:hello@cogniprint.io" className="text-indigo-400 hover:text-indigo-300 transition-colors">
                    hello@cogniprint.io
                  </a>{' '}
                  goes straight to the founder.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="p-3 rounded-lg bg-error/10 border border-error/20 text-error text-sm font-body">
                    {error}
                  </div>
                )}

                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Your name" required>
                    <input
                      type="text"
                      required
                      value={form.name}
                      onChange={(e) => update('name', e.target.value)}
                      className={inputClass}
                    />
                  </Field>
                  <Field label="Work email" required>
                    <input
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => update('email', e.target.value)}
                      className={inputClass}
                    />
                  </Field>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Company" required>
                    <input
                      type="text"
                      required
                      value={form.company}
                      onChange={(e) => update('company', e.target.value)}
                      className={inputClass}
                    />
                  </Field>
                  <Field label="Your role" required>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Head of L&D"
                      value={form.role}
                      onChange={(e) => update('role', e.target.value)}
                      className={inputClass}
                    />
                  </Field>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Team size">
                    <select
                      value={form.teamSize}
                      onChange={(e) => update('teamSize', e.target.value)}
                      className={inputClass}
                    >
                      <option value="under-20">Under 20</option>
                      <option value="20-50">20–50</option>
                      <option value="50-200">50–200</option>
                      <option value="200-1000">200–1000</option>
                      <option value="1000+">1000+</option>
                    </select>
                  </Field>
                  <Field label="Timeline">
                    <select
                      value={form.timeline}
                      onChange={(e) => update('timeline', e.target.value)}
                      className={inputClass}
                    >
                      <option value="this_month">Evaluating now</option>
                      <option value="within_quarter">This quarter</option>
                      <option value="within_half">Within 6 months</option>
                      <option value="just_exploring">Just exploring</option>
                    </select>
                  </Field>
                </div>

                <Field label="What would you want CogniPrint to solve?" helper="A sentence or two — compliance training, onboarding, leadership development, something else.">
                  <textarea
                    rows={4}
                    value={form.useCase}
                    onChange={(e) => update('useCase', e.target.value)}
                    className={`${inputClass} resize-y`}
                  />
                </Field>

                <Button type="submit" className="w-full" loading={submitting}>
                  Request walkthrough
                </Button>

                <p className="text-xs text-content-dim font-body text-center">
                  By submitting, you consent to us contacting you about CogniPrint for Teams.
                  We won't add you to a marketing list without your say-so.
                </p>
              </form>
            )}
          </div>
        </Reveal>

        <Reveal className="mt-6 text-center">
          <Link to="/trust" className="inline-flex items-center gap-1 text-sm text-indigo-400 hover:text-indigo-300 font-body transition-colors">
            Security &amp; compliance details <ArrowRight size={14} />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function Field({
  label,
  helper,
  required,
  children,
}: {
  label: string;
  helper?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-sm font-medium font-body text-content-secondary mb-1">
        {label}{required && <span className="text-error ml-1">*</span>}
      </span>
      {children}
      {helper && <span className="block mt-1 text-[11px] text-content-dim font-body">{helper}</span>}
    </label>
  );
}

export function ForTeamsPage() {
  return (
    <div className="min-h-screen bg-dark-bg relative overflow-hidden">
      <RidgeTexture opacity="opacity-[0.04]" />
      <Hero />
      <SectionDivider />
      <ValueSection />
      <SectionDivider />
      <PilotSection />
      <SectionDivider />
      <PricingSection />
      <SectionDivider />
      <DemoForm />
      <section className="py-8 text-center">
        <a href="mailto:hello@cogniprint.io?subject=CogniPrint Teams enquiry" className="inline-flex items-center gap-1 text-xs text-content-muted hover:text-content font-body transition-colors">
          <Mail size={12} /> Prefer email? hello@cogniprint.io
        </a>
      </section>
      <Footer />
    </div>
  );
}
